import _ from 'lodash';
import { Field, useForm } from 'react-final-form';
import type { ReactSelectOption } from '@/core/IconSelect.molecule';
import { IconSelect } from '@/core/IconSelect.molecule';
import { getFormFieldProps, getFormFieldWrapperProps } from '@/formbuilder/formbuilder.utilities';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import clsx from 'clsx';
import { InputGroup } from '@seeqdev/qomponents';
import { useTranslation } from 'react-i18next';

export interface IconSelectProps extends ValidatingFormComponent<any> {
  component: 'IconSelectFormComponent';
  selectOptions: ReactSelectOption[];
  onChange: (option: { value: any }, property?: string) => void;
  onBlur?: any;
  formattedOptions?: boolean;
  placeholder?: string;
  testId?: string;
  insideModal?: boolean;
  appendToBody?: boolean;
  disabled?: boolean;
  wrapperClasses?: string;
  isMultipleSelect?: boolean;
  isSearchable?: boolean;
  onRemove?: (option: { value: any }) => void;
  // if true component will not be memoized. this should be used SPARINGLY. Should only be necessary if you need to
  // access state that would be stale if your component is memoized (see PeriodicCondition)
  skipMemo?: boolean;
  onTooltipClick?: () => void;
  creatable?: boolean;
  labelClassNames?: string;
  removeAllEnabled?: boolean;
  onRemoveAllClick?: () => void;
  showAddButton?: boolean;
  onClickAddButton?: () => void;
  addButtonTooltip?: string;
  textEntryAllowed?: boolean;
  textToggleDisabled?: boolean;
  showEditButton?: boolean;
  onClickEditButton?: () => void;
  editButtonTooltip?: string;
}

export const IconSelectFormComponent: React.FunctionComponent<IconSelectProps> = (props) => {
  const {
    name,
    validation,
    testId = 'iconSelect',
    wrapperClassNames,
    customErrorText,
    labelClassNames,
    showAddButton = false,
    showEditButton = false,
    onClickAddButton,
    onClickEditButton,
    addButtonTooltip,
    editButtonTooltip,
  } = props;
  const { t } = useTranslation();

  const defaultValidation = _.isEmpty;
  const formState = useForm().getState();
  const showError =
    _.has(formState.errors, name) &&
    formState.hasValidationErrors &&
    ((formState.modified && formState.modified[name]) || formState.submitFailed);

  return (
    <FormFieldWrapper
      {...getFormFieldWrapperProps(props, ['wrapperClassNames', 'testId'])}
      testId={testId}
      labelClassNames={labelClassNames}
      wrapperClassNames={clsx('flexFill', wrapperClassNames)}
      showError={showError}
      customErrorText={customErrorText}>
      <Field name={name} validate={validation || defaultValidation}>
        {({ input, meta }) => {
          if (!showEditButton && !showAddButton) {
            return <IconSelect {...getFormFieldProps(formState, input, meta, props)} testId={`${testId}Field`} />;
          } else {
            return (
              <InputGroup
                field={
                  <IconSelect
                    {...getFormFieldProps(formState, input, meta, props)}
                    testId={`${testId}Field`}
                    wrapperClasses={clsx('width-100percent flexFill')}
                    controlClasses={clsx('no-border')}
                  />
                }
                append={[
                  showAddButton
                    ? {
                        variant: 'button',
                        buttonProps: {
                          icon: 'fa-plus',
                          iconStyle: 'theme',
                          testId: `${testId}AddButton`,
                          onClick: () => {
                            if (onClickAddButton) {
                              onClickAddButton();
                            }
                          },
                          tooltip: t(addButtonTooltip ?? ''),
                        },
                      }
                    : undefined,
                  showEditButton
                    ? {
                        variant: 'button',
                        buttonProps: {
                          icon: 'fa-pencil',
                          iconStyle: 'theme',
                          testId: `${testId}EditButton`,
                          onClick: () => {
                            if (onClickEditButton) {
                              onClickEditButton();
                            }
                          },
                          tooltip: t(editButtonTooltip ?? ''),
                        },
                      }
                    : undefined,
                ]}
              />
            );
          }
        }}
      </Field>
    </FormFieldWrapper>
  );
};
